import * as Sentry from '@sentry/react';

// Build-time values injected by Vite (defined in vite.config.ts)
declare const __BUILD_TIMESTAMP__: string;
declare const __BUILD_RELEASE__: string;

// Environment detection
const getEnvironment = (): 'development' | 'preview' | 'production' => {
  const env = import.meta.env.VITE_APP_ENV;
  if (env === 'production') return 'production';
  if (env === 'preview') return 'preview';
  return 'development';
};

const environment = getEnvironment();
const isEnabled = environment !== 'development';

// Release version from build-time injection or env var fallback
const releaseVersion = typeof __BUILD_RELEASE__ !== 'undefined' 
  ? __BUILD_RELEASE__ 
  : import.meta.env.VITE_SENTRY_RELEASE;
const buildTimestamp = typeof __BUILD_TIMESTAMP__ !== 'undefined' 
  ? __BUILD_TIMESTAMP__ 
  : undefined;

// Sample rates from env with sensible defaults.
// 0.05 across the board after the April 2026 budget remediation. The V1 launch
// observability bump was reverted once span volume started dominating the budget.
const tracesSampleRate = parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '0.05');
// Session Replay: sample 10% of all sessions, but 100% of sessions with errors
const replaysSessionSampleRate = parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE || '0.1');
const replaysOnErrorSampleRate = parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || '1.0');
// Enable replay by default in non-development environments (can be disabled via env var)
const enableReplay = import.meta.env.VITE_SENTRY_ENABLE_REPLAY !== 'false';

// Patterns to scrub from error data
const SENSITIVE_PATTERNS = [
  /api[_-]?key/i,
  /secret/i,
  /password/i,
  /token/i,
  /private[_-]?key/i,
  /auth/i,
  /credential/i,
];

const scrubSensitiveData = (key: string, value: unknown): unknown => {
  if (typeof key === 'string' && SENSITIVE_PATTERNS.some(p => p.test(key))) {
    return '[REDACTED]';
  }
  if (typeof value === 'string' && value.length > 50) {
    // Check if it looks like a key/token
    if (/^[A-Za-z0-9+/=_-]{40,}$/.test(value)) {
      return '[REDACTED_TOKEN]';
    }
  }
  return value;
};

const scrubObject = (obj: Record<string, unknown>): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      result[key] = scrubObject(value as Record<string, unknown>);
    } else {
      result[key] = scrubSensitiveData(key, value);
    }
  }
  return result;
};

export const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  
  if (!dsn || !isEnabled) {
    console.info('[Sentry] Disabled:', !dsn ? 'No DSN configured' : 'Development environment');
    return;
  }

  Sentry.init({
    integrations: enableReplay
      ? [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            maskAllText: false,
            maskAllInputs: true,
            blockAllMedia: false,
            // Block sensitive crypto elements from replay
            block: [
              '[data-sentry-block]',
              '.wallet-address',
              '.private-key',
              '.seed-phrase',
              '.mnemonic',
            ],
            // Mask sensitive inputs and balances
            mask: [
              '[data-sentry-mask]',
              '[data-sensitive]',
              'input[type="password"]',
              'input[name*="key"]',
              'input[name*="secret"]',
              'input[name*="token"]',
              '.tx-signature',
              '.transaction-signature',
              '.private-balance',
              '.sol-balance',
            ],
            // Allow public info to be visible for debugging
            unmask: [
              '.token-symbol',
              '.token-name',
              '.public-info',
              '.pool-address',
            ],
          }),
        ]
      : [Sentry.browserTracingIntegration()],
    dsn,
    environment,
    release: releaseVersion,
    
    // Performance monitoring
    tracesSampleRate,
    
    // Session Replay
    replaysSessionSampleRate,
    replaysOnErrorSampleRate: enableReplay ? replaysOnErrorSampleRate : 0,
    
    // Don't send errors in development
    enabled: isEnabled,
    
    // Scrub sensitive data before sending
    beforeSend(event, hint) {
      // Filter out test artifacts (HeadlessChrome, localhost)
      const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
      const hostname = typeof window !== 'undefined' ? window.location?.hostname : '';

      // Drop HeadlessChrome (Puppeteer MCP) events - these are automated tests
      if (userAgent.includes('HeadlessChrome')) {
        if (import.meta.env.DEV) {
          console.log('[Sentry] Filtered HeadlessChrome event:', event.message || event.exception?.values?.[0]?.value);
        }
        return null;
      }

      // Drop synthetic monitoring bot events (DEXTORO-LAUNCH-90: WebPageTest.org bots fail on Turnkey dynamic imports)
      if (/PTST\/|WebPageTest|Lighthouse|PageSpeed/i.test(userAgent)) {
        return null;
      }
      // Also check Sentry's device context for Spider/Bot classification
      const deviceFamily = (event.contexts?.device as Record<string, unknown>)?.family;
      if (deviceFamily === 'Spider' || deviceFamily === 'Bot') {
        return null;
      }

      // Drop localhost events - local development shouldn't hit production Sentry
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        if (import.meta.env.DEV) {
          console.log('[Sentry] Filtered localhost event:', event.message || event.exception?.values?.[0]?.value);
        }
        return null;
      }

      // Drop TradingView charting_library internal errors (third-party, unfixable)
      const errorFilename = event.exception?.values?.[0]?.stacktrace?.frames?.slice(-1)[0]?.filename || '';
      if (errorFilename.includes('charting_library')) {
        return null;
      }

      // Sample WebSocket noise at 1% (drop 99%) to preserve visibility without burning quota
      const errorMessage = event.exception?.values?.[0]?.value || event.message || '';
      if (/Client WebSocket error|WebSocket connection error/i.test(errorMessage)) {
        if (Math.random() > 0.01) return null;
      }

      // Scrub exception messages
      if (event.exception?.values) {
        event.exception.values = event.exception.values.map(exc => ({
          ...exc,
          value: exc.value?.replace(/[A-Za-z0-9+/=_-]{40,}/g, '[REDACTED_TOKEN]'),
        }));
      }

      // Scrub request data
      if (event.request?.data && typeof event.request.data === 'object') {
        event.request.data = scrubObject(event.request.data as Record<string, unknown>);
      }

      // Scrub breadcrumb data
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map(bc => ({
          ...bc,
          data: bc.data ? scrubObject(bc.data as Record<string, unknown>) : bc.data,
        }));
      }

      return event;
    },
    
    // Filter out noisy errors
    ignoreErrors: [
      // Browser extensions
      /^Script error\.?$/,
      /^ResizeObserver loop/,
      // Network errors that are expected
      /^NetworkError/,
      /^Failed to fetch/,
      // Wallet adapter noise
      /WalletNotConnectedError/,
      /WalletConnectionError/,
      // DOM manipulation errors from wallet modal conflicts (4G/4H/4F/4A)
      // These occur when React's virtual DOM conflicts with wallet modal DOM updates
      /NotFoundError.*removeChild/i,
      /NotFoundError.*insertBefore/i,
      /Failed to execute 'removeChild'/i,
      /Failed to execute 'insertBefore'/i,
      /The node to be removed is not a child/i,
      // Turnkey session errors - expected when session expires
      /No active session/i,
      /Failed to refresh session/i,
      /session expired/i,
      /session not found/i,
      // User-cancelled actions
      /cancelled by the user/i,
      /user rejected/i,
      /window was closed/i,
      // Cross-origin iframe errors (Turnkey auth iframe)
      /cross-origin frame/i,
      /Blocked a frame/i,
      // Abort signals (navigation, cancelled requests)
      /signal is aborted/i,
      /AbortError/i,
      // Old browser compatibility (Safari <15.4, old Chrome)
      /structuredClone is not defined/i,
      /randomUUID is not a function/i,
      // URL construction errors (often from wallet adapters with stale/invalid config)
      /Failed to construct 'URL'/i,
      /Invalid URL/i,
      // Stale chunk errors are no longer ignored. Four self-heal layers
      // (CDN _not-found.js, vite:preloadError, lazyWithRetry, bootstrap
      // watchdog) reload the page before Sentry captures anything. Events
      // that still reach Sentry mean all recovery failed and the user is
      // stuck. A Sentry alert rule monitors for these spikes.
      // Third-party scripts (jam.dev) on browsers without SharedWorker support
      /SharedWorker is not defined/i,
      // User-initiated Turnkey signing cancellation
      /User canceled the signing process/i,
      // TradingView internal schema validation warnings
      /does not match a schema/i,
      // iOS Safari pointer event null target (react-aria/wallet-adapter third-party bug)
      /null is not an object \(evaluating '.*\.target'\)/i,
      // Expected user errors - insufficient balance during transactions
      /Insufficient balance to complete this transaction/i,
      /Simulation failed/i,
      // iOS Safari IndexedDB connection loss (Turnkey SDK uses IndexedDB for wallet keys)
      /Connection to Indexed Database server lost/i,
      // FILTER: Supabase Realtime channel reconnection errors
      // Reason: CHANNEL_ERROR fires whenever a Realtime channel retries its
      //   WebSocket connection. It is the normal lifecycle of reconnects
      //   (visibility changes, network blips, server-side rebalancing). The
      //   new_coin_realtime channel provider handles these internally with
      //   exponential backoff; Sentry capture adds no signal.
      // Added: 2026-04-18 as part of Sentry budget remediation
      // Source issue: SENTRY-dextoro-launch-BG (spike 00:00-06:00 EDT 2026-04-18)
      // Volume impact: reduces ~thousands of events/day per user session
      /CHANNEL_ERROR/,
      /new_coin_realtime/i,
      // FILTER: Solana blockhash expiry during transaction confirmation
      // Reason: "blockhash not found" is an expected transient when a tx's
      //   recent blockhash ages out before confirmation. The trade pipeline
      //   already handles this by re-fetching a fresh blockhash and retrying.
      //   Mirrors the MM hardening from 2026-04-17.
      // Added: 2026-04-18 as part of Sentry budget remediation
      // Source issue: preventive hardening, mirrors MM (dextoro-market-maker 068be21)
      // Volume impact: prevents future flares during RPC or validator rollover
      /blockhash not found/i,
    ],

    // Add useful context tags
    initialScope: {
      tags: {
        app: 'memelaunch',
        rpc: 'unknown',
        cluster: import.meta.env.VITE_USE_DEVNET === 'true' ? 'devnet' : 'mainnet-beta',
        ...(buildTimestamp && { build_time: buildTimestamp }),
      },
    },
  });

  console.info(`[Sentry] Initialized for ${environment}`, releaseVersion ? `release: ${releaseVersion}` : '');
};

// Re-export Sentry for use elsewhere
export { Sentry };

// Utility to capture errors with context
export const captureError = (error: Error, context?: Record<string, unknown>) => {
  if (context) {
    Sentry.setContext('additional', scrubObject(context));
  }
  Sentry.captureException(error);
};

// Utility to add breadcrumb
export const addBreadcrumb = (message: string, category: string, data?: Record<string, unknown>) => {
  Sentry.addBreadcrumb({
    message,
    category,
    data: data ? scrubObject(data) : undefined,
    level: 'info',
  });
};

// Set user identity for error tracking (uses wallet address as identifier)
export const setUser = (walletAddress: string | null) => {
  if (walletAddress) {
    Sentry.setUser({ 
      id: walletAddress.slice(0, 8), // Use shortened address for privacy
      username: walletAddress.slice(0, 8),
    });
  } else {
    Sentry.setUser(null);
  }
};

// Clear user identity (call on logout)
export const clearUser = () => {
  Sentry.setUser(null);
};

// ============================================================================
// CRYPTO-SPECIFIC CONTEXT HELPERS (v1 Launch)
// ============================================================================

export type TransactionType = 'swap' | 'buy' | 'sell' | 'create_token' | 'claim_fees' | 'transfer';
export type ConfirmationStatus = 'preparing' | 'signing' | 'sending' | 'confirming' | 'confirmed' | 'finalized' | 'failed';

/**
 * Set transaction context for debugging crypto operations.
 * Call this before/during transaction flows to capture relevant state.
 */
export const setTransactionContext = (data: {
  type: TransactionType;
  mint?: string;
  amountSol?: number;
  amountTokens?: number;
  signature?: string;
  rpcProvider?: 'rpc' | 'triton' | 'alchemy' | 'unknown' | 'custom';
  status?: ConfirmationStatus;
  poolAddress?: string;
}) => {
  Sentry.setContext('transaction', {
    type: data.type,
    mint: data.mint,
    amount_sol: data.amountSol,
    amount_tokens: data.amountTokens,
    signature: data.signature ? `${data.signature.slice(0, 8)}...` : undefined, // Truncate for privacy
    rpc_provider: data.rpcProvider || 'unknown',
    status: data.status,
    pool_address: data.poolAddress,
    timestamp: new Date().toISOString(),
  });

  // Also set as tag for easy filtering
  Sentry.setTag('tx_type', data.type);
  if (data.status) {
    Sentry.setTag('tx_status', data.status);
  }
};

/**
 * Set wallet context for debugging auth/wallet issues.
 * Call this on wallet connect/disconnect and during wallet operations.
 */
export const setWalletContext = (data: {
  provider: 'turnkey' | 'phantom' | 'solflare' | 'other';
  addressPrefix?: string;
  hasSession?: boolean;
  isEmbedded?: boolean;
}) => {
  Sentry.setContext('wallet', {
    provider: data.provider,
    address_prefix: data.addressPrefix,
    has_active_session: data.hasSession,
    is_embedded: data.isEmbedded,
  });

  Sentry.setTag('wallet_provider', data.provider);
};

/**
 * Set RPC context when RPC operations fail.
 * Helps diagnose RPC-specific issues.
 */
export const setRpcContext = (data: {
  provider: 'rpc' | 'triton' | 'alchemy' | 'unknown' | 'custom';
  method?: string;
  latencyMs?: number;
  error?: string;
  endpoint?: string;
}) => {
  // Safely extract host from endpoint URL
  let endpointHost: string | undefined;
  if (data.endpoint) {
    try {
      endpointHost = new URL(data.endpoint).host;
    } catch {
      endpointHost = '(invalid)';
    }
  }

  Sentry.setContext('rpc', {
    provider: data.provider,
    method: data.method,
    latency_ms: data.latencyMs,
    error: data.error,
    endpoint_host: endpointHost,
  });

  Sentry.setTag('rpc_provider', data.provider);
  if (data.method) {
    Sentry.setTag('rpc_method', data.method);
  }
};

/**
 * Start a performance span for tracking key user flows.
 * Returns a span that should be finished when the operation completes.
 *
 * @example
 * const span = startPerformanceSpan('token-creation', 'user.action');
 * try {
 *   await createToken();
 *   span?.end();
 * } catch (err) {
 *   span?.setStatus({ code: 2, message: 'failed' });
 *   span?.end();
 *   throw err;
 * }
 */
export const startPerformanceSpan = (name: string, op: string) => {
  return Sentry.startInactiveSpan({
    name,
    op,
    forceTransaction: true,
  });
};

/**
 * Start a span for tracing operations.
 * Wrapper around Sentry.startInactiveSpan for simpler API.
 *
 * @example
 * const span = startSpan({ name: 'trade.quote' });
 * try {
 *   await doWork();
 *   span?.setAttributes({ key: 'value' });
 *   span?.end();
 * } catch (err) {
 *   span?.end();
 *   throw err;
 * }
 */
export const startSpan = (options: { name: string; op?: string }) => {
  return Sentry.startInactiveSpan({
    name: options.name,
    op: options.op || 'function',
  });
};

/**
 * Capture a transaction-specific error with full context.
 * Use this for buy/sell/create failures.
 */
export const captureTransactionError = (
  error: Error,
  txData: {
    type: TransactionType;
    mint?: string;
    signature?: string;
    status?: ConfirmationStatus;
  }
) => {
  setTransactionContext(txData);
  Sentry.captureException(error, {
    tags: {
      feature: 'transaction',
      tx_type: txData.type,
    },
    level: 'error',
  });
};

/**
 * Capture an RPC-specific error with context.
 * Use this for RPC failures, timeouts, rate limits.
 */
export const captureRpcError = (
  error: Error,
  rpcData: {
    provider: 'rpc' | 'triton' | 'alchemy' | 'unknown' | 'custom';
    method?: string;
    latencyMs?: number;
  }
) => {
  setRpcContext(rpcData);
  Sentry.captureException(error, {
    tags: {
      feature: 'rpc',
      rpc_provider: rpcData.provider,
    },
    level: 'error',
  });
};
